import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PLAN_FEATURES, effectivePlan } from '@unipostudio/platform-constants';
import { api } from '@/shared/services/api';
import { useWorkspaceStore } from '@/shared/stores/workspace.store';

/**
 * The white-label branding the app shell applies for the current workspace,
 * exactly as `GET /branding/applied` answers it.
 *
 * Only the logo, the favicon and the "Powered by" line reach the shell. The
 * workspace's accent colour does not re-theme our controls: it is for the
 * surfaces its clients see (the notification emails), and the shell keeps
 * the aubergine its contrast was checked against.
 */
export interface AppliedBranding {
  applied: boolean;
  logoUrl: string | null;
  faviconUrl: string | null;
  showPoweredBy: boolean;
}

export const UNBRANDED: AppliedBranding = {
  applied: false,
  logoUrl: null,
  faviconUrl: null,
  showPoweredBy: true,
};

/** Under the settings screen's ['branding'] key, so saving branding refreshes the shell too. */
export const APPLIED_BRANDING_KEY = ['branding', 'applied'] as const;

/** Only an absolute http(s) URL becomes an <img src> or a favicon. */
function safeUrl(url: string | null): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? url : null;
  } catch {
    return null;
  }
}

/**
 * The branding to apply for the current workspace. Workspaces whose paid plan
 * has no white label never ask; the server decides for the rest, from the
 * paid plan, so an Agency workspace back on Starter goes back to UniPostudio.
 */
export function useAppliedBranding(): AppliedBranding {
  const workspace = useWorkspaceStore((s) => s.currentWorkspace);
  const mayApply = workspace ? PLAN_FEATURES[effectivePlan(workspace.subscription)].whiteLabel : false;

  const { data } = useQuery({
    queryKey: [...APPLIED_BRANDING_KEY, workspace?.id ?? null],
    queryFn: async (): Promise<AppliedBranding> => {
      const res = await api.get<AppliedBranding>('/branding/applied');
      return res.data;
    },
    enabled: mayApply,
    staleTime: 1000 * 60 * 5,
  });

  if (!mayApply || !data?.applied) return UNBRANDED;
  return {
    applied: true,
    logoUrl: safeUrl(data.logoUrl),
    faviconUrl: safeUrl(data.faviconUrl),
    showPoweredBy: data.showPoweredBy,
  };
}

/** The id of the favicon link index.html ships with. */
export const FAVICON_LINK_ID = 'app-favicon';

/**
 * Points the tab icon at the workspace's favicon while one applies, and puts
 * UniPostudio's back when it stops applying (a workspace switch, a plan that ended).
 */
export function useBrandFavicon(faviconUrl: string | null): void {
  useEffect(() => {
    if (!faviconUrl) return undefined;
    let link = document.getElementById(FAVICON_LINK_ID) as HTMLLinkElement | null;
    const created = !link;
    if (!link) {
      link = document.createElement('link');
      link.id = FAVICON_LINK_ID;
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    const previousHref = link.getAttribute('href');
    const previousType = link.getAttribute('type');
    link.removeAttribute('type');
    link.href = faviconUrl;

    return () => {
      if (!link) return;
      if (created) {
        link.remove();
        return;
      }
      if (previousHref !== null) link.setAttribute('href', previousHref);
      if (previousType !== null) link.setAttribute('type', previousType);
    };
  }, [faviconUrl]);
}
